// Bond pricing helper used by BuyBack. Keep GSec component untouched.
import { getDaysDifference, formatDate, excelPRICE, parseLocalYMD, findCouponPeriodFromMaturity } from './gsecUtils';

// Truncate to 4 decimals (not round) - matching GSec form logic
function truncate4(value) {
  const num = Number(value || 0);
  return Math.floor(num * 10000) / 10000;
}

// Compute per-100 clean/dirty/accrued from yield using Actual/Actual and semi-annual coupons
// This function now matches exactly the GSec form's calculatePrices logic
export function pricePer100FromYield({ couponRate, yieldRate, valueDate, issueDate, maturityDate, couponDate1, couponDate2 }) {
  if (!couponRate || !yieldRate || !valueDate || !maturityDate || !issueDate || !couponDate1 || !couponDate2) {
    return { dirtyPrice: '', cleanPrice: '', accruedInterestPer100: '' };
  }

  // Fixed semi-annual frequency - matching GSec form
  const frequency = 2;
  const fv = 100; // per 100 basis
  const cr = parseFloat(couponRate) / 100;
  const ytm = parseFloat(yieldRate) / 100;
  // Use local date parsing to avoid timezone-induced day shifts (JS treats YYYY-MM-DD as UTC).
  const settle = parseLocalYMD(valueDate);
  const maturity = parseLocalYMD(maturityDate);

  const coupon = fv * cr / frequency;
  const ytmPerPeriod = ytm / frequency;
  const monthsPerPeriod = 12 / frequency;

  const { lastCoupon, nextCoupon } = findCouponPeriodFromMaturity(settle, maturity, frequency);

  // Calculate exact days in current coupon period using Actual/Actual day count
  const daysInPeriod = getDaysDifference(nextCoupon, lastCoupon);
  const daysToNextCoupon = getDaysDifference(nextCoupon, settle);
  const fractionalPeriod = daysToNextCoupon / daysInPeriod;

  // Step 2: Generate all future cash flows from next coupon to maturity
  const cashFlows = [];
  let cfDate = new Date(nextCoupon);
  let periodCount = 0;

  while (cfDate <= maturity) {
    const isFinal = cfDate.getTime() === maturity.getTime();
    const amount = isFinal ? coupon + fv : coupon;
    cashFlows.push({
      date: new Date(cfDate),
      amount,
      periodCount
    });
    cfDate.setMonth(cfDate.getMonth() + monthsPerPeriod);
    periodCount++;
  }

  // Step 3: Calculate PV of each cash flow using Actual/Actual day count basis and precise t values
  let dirtyPrice = 0;
  for (const cf of cashFlows) {
    // Calculate precise t = fractionalPeriod + integer number of periods
    const t = fractionalPeriod + cf.periodCount;
    
    // Calculate present value using the precise t value and semi-annual discounting
    const pv = cf.amount / Math.pow(1 + ytmPerPeriod, t);
    dirtyPrice += pv;
  }

  // Calculate clean price using Excel PRICE function (Actual/Actual basis)
  // Excel PRICE: settlement, maturity, rate, yld, redemption, frequency, basis
  const excelCleanPrice = excelPRICE(
    settle,           // settlement (value date)
    maturity,         // maturity date
    cr,               // coupon rate (decimal)
    ytm,              // yield (decimal)
    100,              // redemption value per 100
    frequency,        // frequency (2 = semi-annual)
    1                 // basis (1 = Actual/Actual)
  );

  // Use Excel PRICE clean price if valid, otherwise fall back to NPV calculation
  let roundedCleanPrice;
  if (!isNaN(excelCleanPrice) && isFinite(excelCleanPrice)) {
    // Round clean price to 4 decimal places
    roundedCleanPrice = Math.round(excelCleanPrice * 10000) / 10000;
  } else {
    // Fallback: Clean price from NPV dirty price minus accrued interest
  const dirtyPrice10000 = Math.floor(dirtyPrice * 10000 + 0.0001);
  const truncatedDirtyPrice = dirtyPrice10000 / 10000;
    const daysAccrued = getDaysDifference(settle, lastCoupon);
    const accruedInterest = coupon * (daysAccrued / daysInPeriod);
    const roundedAccruedInterestPer100 = Math.round(accruedInterest * 10000) / 10000;
    const cleanPrice = truncatedDirtyPrice - roundedAccruedInterestPer100;
    roundedCleanPrice = Math.round(cleanPrice * 10000) / 10000;
  }

  // Calculate accrued interest (per 100) - rounded to 4 decimals
  const daysAccrued = getDaysDifference(settle, lastCoupon);
  const accruedInterest = coupon * (daysAccrued / daysInPeriod);
  // Round accrued interest to 4 decimal places
  const roundedAccruedInterestPer100 = Math.round(accruedInterest * 10000) / 10000;

  // Calculate dirty price = Excel Clean Price (rounded) + Accrued Interest (rounded)
  // Use Math.round because both addends are already rounded to 4dp; any sub-unit
  // deviation is purely floating-point noise (e.g. 98.2237+3.5735=101.79719999…).
  const dirtyPriceFromExcel = roundedCleanPrice + roundedAccruedInterestPer100;
  const truncatedDirtyPrice = Math.round(dirtyPriceFromExcel * 10000) / 10000;

  return {
    dirtyPrice: truncatedDirtyPrice.toFixed(4),
    cleanPrice: roundedCleanPrice.toFixed(4),
    accruedInterestPer100: roundedAccruedInterestPer100.toFixed(4)
  };
}

// Reverse: from settlement amount and yield to face value
export function faceValueFromSettlement({ settlementAmount, couponRate, yieldRate, valueDate, issueDate, maturityDate, couponDate1, couponDate2 }) {
  const { dirtyPrice, cleanPrice, accruedInterestPer100 } = pricePer100FromYield({ couponRate, yieldRate, valueDate, issueDate, maturityDate, couponDate1, couponDate2 });
  const dirty = parseFloat(dirtyPrice);
  if (!dirty || isNaN(dirty)) {
    return { faceValue: '', dirtyPricePer100: '', cleanPricePer100: '', accruedInterestPer100: '' };
  }
  
  // Calculate face value using the same formula as GSec form's settlement amount calculation
  // GSec: settlementAmount = (faceValue * dirtyPrice / 100)
  // So: faceValue = (settlementAmount * 100) / dirtyPrice
  const fv = (Number(settlementAmount) * 100) / dirty;
  
  return {
    faceValue: isFinite(fv) ? fv.toFixed(2) : '',
    dirtyPricePer100: dirtyPrice,
    cleanPricePer100: cleanPrice,
    accruedInterestPer100
  };
}

// Forward: from face value and settlement amount to yield (using bisection method for better stability)
export function yieldFromFaceValueAndSettlement({ faceValue, settlementAmount, couponRate, valueDate, issueDate, maturityDate, couponDate1, couponDate2 }) {
  if (!faceValue || !settlementAmount || !couponRate || !valueDate || !issueDate || !maturityDate || !couponDate1 || !couponDate2) {
    return { yieldRate: '', dirtyPrice: '', cleanPrice: '', accruedInterestPer100: '' };
  }

  const fv = parseFloat(faceValue);
  const settlement = parseFloat(settlementAmount);
  
  console.log('Yield calculation inputs:', { fv, settlement, couponRate, valueDate, issueDate, maturityDate, couponDate1, couponDate2 });
  
  if (!fv || !settlement || isNaN(fv) || isNaN(settlement)) {
    console.error('Invalid face value or settlement amount:', { fv, settlement });
    return { yieldRate: '', dirtyPrice: '', cleanPrice: '', accruedInterestPer100: '' };
  }

  // Target dirty price per 100 = (settlement * 100) / faceValue
  const targetDirtyPricePer100 = (settlement * 100) / fv;
  
  console.log('Starting yield calculation. Target dirty price per 100:', targetDirtyPricePer100);

  // Use bisection method for more stable convergence
  let yieldLow = 0.001; // 0.1% minimum yield
  let yieldHigh = 0.50;  // 50% maximum yield
  const tolerance = 0.0001; // 0.01% tolerance
  const maxIterations = 100;

  // Helper function to get price for a given yield
  const getPriceForYield = (yieldRate) => {
    const result = pricePer100FromYield({
      couponRate,
      yieldRate: yieldRate * 100, // Convert to percentage
      valueDate,
      issueDate,
      maturityDate,
      couponDate1,
      couponDate2
    });
    return parseFloat(result.dirtyPrice);
  };

  // Check if target is within bounds
  const priceLow = getPriceForYield(yieldLow);
  const priceHigh = getPriceForYield(yieldHigh);
  
  console.log('Price bounds check:', { yieldLow, priceLow, yieldHigh, priceHigh, target: targetDirtyPricePer100 });
  
  if (isNaN(priceLow) || isNaN(priceHigh)) {
    console.error('Invalid price calculation at bounds');
    return { yieldRate: '', dirtyPrice: '', cleanPrice: '', accruedInterestPer100: '' };
  }

  // Check if target is within the price range
  if (targetDirtyPricePer100 < Math.min(priceLow, priceHigh) || targetDirtyPricePer100 > Math.max(priceLow, priceHigh)) {
    console.error('Target price is outside feasible range:', { target: targetDirtyPricePer100, min: Math.min(priceLow, priceHigh), max: Math.max(priceLow, priceHigh) });
    // Still try to find the closest solution
  }

  // Bisection method
  for (let i = 0; i < maxIterations; i++) {
    const yieldMid = (yieldLow + yieldHigh) / 2;
    const priceMid = getPriceForYield(yieldMid);
    
    if (isNaN(priceMid)) {
      console.error('Invalid price calculation at yield:', yieldMid);
      break;
    }

    const priceDiff = priceMid - targetDirtyPricePer100;
    
    console.log(`Iteration ${i}: yield=${yieldMid.toFixed(6)}, price=${priceMid.toFixed(4)}, target=${targetDirtyPricePer100.toFixed(4)}, diff=${priceDiff.toFixed(4)}`);
    
    if (Math.abs(priceDiff) < tolerance) {
      // Found the yield! Get the final result with all pricing details
      const finalResult = pricePer100FromYield({
        couponRate,
        yieldRate: yieldMid * 100,
        valueDate,
        issueDate,
        maturityDate,
        couponDate1,
        couponDate2
      });
      
      console.log('Yield calculation converged:', { yield: yieldMid * 100, result: finalResult });
      
      return {
        yieldRate: (yieldMid * 100).toFixed(6), // Convert back to percentage
        dirtyPrice: finalResult.dirtyPrice,
        cleanPrice: finalResult.cleanPrice,
        accruedInterestPer100: finalResult.accruedInterestPer100
      };
    }

    // Update bounds for next iteration
    // Since higher yield typically means lower price (inverse relationship)
    if (priceMid > targetDirtyPricePer100) {
      yieldLow = yieldMid; // Need higher yield to get lower price
    } else {
      yieldHigh = yieldMid; // Need lower yield to get higher price
    }
    
    // Prevent infinite loop if bounds become too close
    if (Math.abs(yieldHigh - yieldLow) < 0.000001) {
      console.log('Yield bounds converged');
      break;
    }
  }

  // If we didn't converge, return the best guess
  const finalYield = (yieldLow + yieldHigh) / 2;
  const finalResult = pricePer100FromYield({
    couponRate,
    yieldRate: finalYield * 100,
    valueDate,
    issueDate,
    maturityDate,
    couponDate1,
    couponDate2
  });
  
  console.warn('Yield calculation did not fully converge. Using best approximation:', finalYield * 100);
  
  return {
    yieldRate: (finalYield * 100).toFixed(6),
    dirtyPrice: finalResult.dirtyPrice,
    cleanPrice: finalResult.cleanPrice,
    accruedInterestPer100: finalResult.accruedInterestPer100
  };
}


